import React, { useState, useEffect, useRef } from 'react'
import { SearchItem, SearchListCallbacks } from './searchlistCallbacks'
import Dialog from './dialog';
import parse from 'html-react-parser';
declare let window: any

interface HLServerSearchListDialogProps {
    elementObj?: any;
    onClose: () => void;
    isOpen: boolean;
    callbacks: SearchListCallbacks; // Generic callbacks for find, save, etc.
    searchListType?: 'CheckBox' | 'RadioButton'; 
    maxItems?: number; 
}

const HLServerSearchListDialog: React.FC<HLServerSearchListDialogProps> = ({
    elementObj,
    onClose,
    isOpen,
    callbacks,
    searchListType = 'CheckBox',
    maxItems = 100,
}) => {

    const [keyValues, setKeyValues] = useState<SearchItem[]>([]);
    const [selectedItems, setSelectedItems] = useState<SearchItem[]>([]);
    const [filterText, setFilterText] = useState('');
    const [isSelectAllDisplayed, setIsSelectAllDisplayed] = useState<boolean>(false);
    const [displayLabel, setDisplayLabel] = useState<boolean>(false);
    const [countOfUsersSelected, setCountOfUsersSelected] = useState<number>(0);
    const [searchMessage, setSearchMessage] = useState<string | null>(null); // State for search message

    useEffect(() => {
        if (isOpen && elementObj && searchListType === 'CheckBox') {
            setSelectedItems(elementObj);
        }
            
            if (!isOpen ) {
                // Reset everything except selectedItems when closing the dialog
                setKeyValues([]);
                setFilterText('');
                setSearchMessage(null);
                setCountOfUsersSelected(0);
                setIsSelectAllDisplayed(false);
                setDisplayLabel(false);
            }
            if(!isOpen && searchListType === 'RadioButton') {
                setSelectedItems([]);
            }
        }, [isOpen, elementObj]);

        const handleSave = () => {
            onClose(); 
            callbacks.onSave(selectedItems.slice(0, maxItems)) 
        }
    

    const handleSelectAll = () => {
        const allDisplayedItems = keyValues.map((item) => ({
            KeyValue: item.KeyValue,
            DisplayText: item.DisplayText,
            DisplayDesc: item.DisplayDesc,
        }));
    
        setSelectedItems((prevSelectedItems) => {
            // Merge previous selections with new selections, avoiding duplicates
            const updatedSelection = [...prevSelectedItems, ...allDisplayedItems].reduce<SearchItem[]>((acc, item) => {
                if (!acc.some((existing) => existing.KeyValue === item.KeyValue)) {
                    acc.push(item);
                }
                return acc;
            }, []);
    
            return updatedSelection;
        });
    
        if (callbacks.onSelectAll) {
            callbacks.onSelectAll(allDisplayedItems);
        }
    };
    
    const handleClearAll = () => {
        setSelectedItems([]);
        if (callbacks.onClearAll) {
            callbacks.onClearAll();
        }
    }

    const handleFindClick = async () => {
        try {
            const filteredData = await callbacks.onFind(filterText);
    
            if (Array.isArray(filteredData)) {
                if (filteredData.length === 0) {
                    setSearchMessage("Sorry, no matches found. Try a different search.");
                    setKeyValues([]);
                    setIsSelectAllDisplayed(false);
                } else {
                    setKeyValues(filteredData.slice(0, maxItems));
                    setSearchMessage(null);
                    setCountOfUsersSelected(filteredData.length);
                }
            } else if (typeof filteredData === "string") {
                setSearchMessage(filteredData); // Set the message when no matches are found
                setKeyValues([]); // Clear previous search results
                setCountOfUsersSelected(0);
                setIsSelectAllDisplayed(false);

            } else {
                console.error("Unexpected result type:", filteredData);
            }
    
            if (typeof filteredData !== "string") {
                if (searchListType === "CheckBox") {
                    setIsSelectAllDisplayed(true);
                } else if (searchListType === "RadioButton") {
                    setDisplayLabel(true);
                }
            }
        } catch (error) {
            console.error("Error fetching filtered data:", error);
            setSearchMessage("An error occurred while searching. Please try again.");
        }
    };
   
    const toggleSelection = (item: SearchItem) => {
        if (searchListType === 'CheckBox') {
            setSelectedItems((prevSelectedItems) => {
                const isAlreadySelected = prevSelectedItems.some(
                    (selectedItem) => selectedItem.KeyValue === item.KeyValue
                );

                if (isAlreadySelected) {
                    // If the item is already selected, remove it
                    return prevSelectedItems.filter(
                        (selectedItem) => selectedItem.KeyValue !== item.KeyValue
                    );
                } else {
                    // If the item is not selected, add it
                    return [...prevSelectedItems, item];
                }
            });
        } else if (searchListType === 'RadioButton') {
            setSelectedItems([item]);
        }
    };

    return (
        <Dialog
            isOpen={isOpen}
            title="Search And Select List"
            onClose={onClose}
            width="90vw"
            height="auto"
            maxWidth={602}
            className="left-align-text"
            buttons={[
                {
                    label: 'Cancel',
                    className: 'ui-button ui-corner-all ui-widget app-button negative',
                    onClick: onClose,
                },
                {
                    label: 'Save',
                    className: 'ui-button ui-corner-all ui-widget app-button',
                    onClick: handleSave,
                },
            ]}
        >
            <div
                id="ui_serversearch_list_container"
                className="vu-popup-searchlist-props hide ui-dialog-content ui-widget-content"
                style={{
                    display: 'block',
                    width: 'auto',
                    minHeight: '43.6px',
                    height: 'auto',
                    boxSizing: 'border-box',
                }}
            >
                {/* Search Input and Find Button */}
                <div className="searchlist-popup-find-wrapper">
                    <div className="searchlist-popup-header">
                        <input
                            type="text"
                            id="ui_serversearch_filter"
                            value={filterText}
                            style={{ fontSize: '14px' }}
                            size={40}
                            placeholder="enter a few letters and click find"
                            // handle find when user clicks enter in search box - Steven 
                            onKeyDown={(event)=>{
                                if (event.key === 'Enter') {
                                    handleFindClick();
                                }
                            }}
                            onChange={(e) => {
                                e.stopPropagation();
                                setFilterText(e.target.value);
                            }}
                        />
                        <span>&nbsp;&nbsp;</span>
                        <button
                            type="button"
                            id="ui_serversearch_button"
                            className="ui-button app-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleFindClick();
                            }}
                        >
                            Find
                        </button>
                    </div>

                    {/* Search Results List */}
                    <ul
                        id="ui_serversearch_list"
                        className="filtervalue-list searchlist-popup-list filtervalue-list-multiple"

                    >
                        {keyValues.length > 0 ? (
                            keyValues.map((item) => (
                                <li
                                id={`ui_serversearch_filtervalue_item_${item.KeyValue}`}
                                    key={item.KeyValue}
                                    className={
                                        
                                        selectedItems.some((i) => i.KeyValue === item.KeyValue)
                                            ? 'selected'
                                            : 'not-selected'
                                    }
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'flex-start', 
                                        gap: '1px'  
                                    }}
                                    onClick={(e) => {if ((e.target as HTMLElement).tagName !== "INPUT") {
                                        toggleSelection(item);
                                    }}}
                                >
                                    {/* Checkbox or Radio Button */}
                                    {searchListType === 'CheckBox' ? (
                                        <div
                                            className="float-left fieldcheckbox"
                                         
                                          
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedItems.some(
                                                    (i) => i.KeyValue === item.KeyValue
                                                )}
                                                onChange={(e) => {
                                                    toggleSelection(item);
                                                }}
                                            />
                                        </div>
                                    ) : searchListType === 'RadioButton' ? (
                                        <div className="fieldcheckbox"   style={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start' 
                                        }}>
                                            <input
                                                type="radio"
                                                checked={selectedItems.some(
                                                    (i) => i.KeyValue === item.KeyValue
                                                )}
                                                onChange={(e) => {
                                                    toggleSelection(item);
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="error">Unknown input type</div>
                                    )}

                                    {/* Item Content */}
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1,
                                        marginLeft: '5px',
                                    }}>
                                        <div className="float-left fieldcontent">
                                            {parse(item.DisplayText)}
                                        </div>
                                        
                                        {item.isSummarydiv ? (
                                            <div className="float-left fieldcontent isSummarydiv">
                                                {item.DisplayDesc || item.Description}
                                            </div>
                                        ) : <div className="float-left fieldcontent sub-info">
                                            <i>{item.DisplayDesc || item.Description}</i>
                                        </div>
                                        }

                                    </div>
                                </li>
                            ))
                        ) : searchMessage ? (
                            <li className="info no-match-message">
                                    {searchMessage}
                            </li>
                        ) : null} 
                    </ul>


                    {/* Select All Displayed */}
                    {isSelectAllDisplayed && (
                        <div
                            id="ui_serversearchlist_list_filter_options"
                            className="vu-popup-filter-options-1"
                        >   
                        {countOfUsersSelected >= 100 &&(
                            <span id="ui_serversearchlist_list_toomany">
                            showing only{' '}
                            {countOfUsersSelected < 100 ? countOfUsersSelected : 100} of{' '}
                        </span>
                        )}
                            {keyValues.length > 0 && (
                            <span id="ui_serversearchlist_list_count">
                                {countOfUsersSelected} items
                            </span>
                        )}
                            {keyValues.length > 0 && (
                            <span id="dot"> • </span>
                            )}
                            {keyValues.length > 0 && (
                                <span id="link">
                                <a
                                    href="javascript:void(0);"
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                        handleSelectAll();
                                    }}
                                >
                                    select all displayed
                                </a>
                            </span>
                            )}
                        </div>
                    )}

                    {/* Display Label */}
                    {displayLabel && (
                        <div
                            id="ui_serversearchlist_list_filter_options"
                            className="vu-popup-filter-options-1"
                            style={{ display: 'none;' }}
                        >
                            <span id="ui_serversearchlist_list_toomany">
                                showing only{' '}
                                {countOfUsersSelected < 100 ? countOfUsersSelected : 100} of{' '}
                            </span>
                            <span id="ui_serversearchlist_list_count">
                                {countOfUsersSelected} items
                            </span>
                            <span id="dot"> • </span>
                        </div>
                    )}
                </div>

                {/* Selected Items List */}
                <div className="searchlist-popup-sel-wrapper">
                    <div className="vu-popup-element-subheader padding-top-largest">
                        Selected Values
                    </div>
                    <ul
                        id="ui_searchlist_sel_list"
                        className="filtervalue-list searchlist-popup-list filtervalue-list-multiple"
                    >
                        {selectedItems.map((item) => (
                            <li
                                key={item.KeyValue}
                                id={`ui_searchlist_filtervalue_item_sel_${item.KeyValue}`}
                                className={`selected ${item.isSoftwareRestricted ? 'softwarerestricted' : ''}`}
                                title={
                                    item.isSoftwareRestricted
                                        ? 'Cannot detach software restricted report'
                                        : ''
                                }
                                onClick={(e) => {
                                    toggleSelection(item);
                                }}
                            >
                                
                                {/* Checkbox or Radio Button */}
                                {searchListType === 'CheckBox' ? (
                                    <div className="float-left fieldcheckbox">
                                        <input
                                            type="checkbox"
                                            id={`ui_searchlist_filtervalue_checkbox_sel_${item.KeyValue}`}
                                            name="filtervalue_checkbox_sel"
                                            checked={selectedItems.some(
                                                (selected) => selected.KeyValue === item.KeyValue
                                            )}
                                            value={item.KeyValue}
                                            disabled={item.isSoftwareRestricted}
                                        />
                                    </div>
                                ) : (
                                    <div className="float-left fieldcheckbox">
                                        <input
                                            type="radio"
                                            id={`ui_searchlist_filtervalue_radiobutton_sel_${item.KeyValue}`}
                                            name="filtervalue_radiobutton_sel"
                                            checked={selectedItems.some(
                                                (selected) => selected.KeyValue === item.KeyValue
                                            )}
                                            value={item.KeyValue}
                                            disabled={item.isSoftwareRestricted}
                                        />
                                    </div>
                                )}

                                {/* Item Content */}
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1,
                                        marginLeft: '5px',
                                    }}
                                >
                                    <div className="float-left fieldcontent">
                                        {parse(item.DisplayText)}
                                    </div>

                                    {item.isSummarydiv ? (
                                        <div className="float-left fieldcontent isSummarydiv">
                                            {item.DisplayDesc || item.Description}
                                        </div>
                                        ) : 
                                        <div className="float-left fieldcontent sub-info">
                                            <i>{item.DisplayDesc || item.Description}</i>
                                        </div>
                                    }
                                </div>
                                <div className="clear-both"></div>
                            </li>
                        ))}
                    </ul>

                    {/* Clear All Button */}
                  
                        <div
                            id="ui_searchlist_sel_list_filter_options"
                            className="vu-popup-filter-options-1"
                            style={{ display: 'block' }}
                        >
                            <span id="ui_serversearchlist_selected_count">
                                {selectedItems.length} items •{' '}
                            </span>
                            <a
                                href="javascript:void(0);"
                                id="ui_serversearchlist_selected_clearall"
                                onClick={handleClearAll}
                            >
                                clear all
                            </a>
                        </div>
                    
                </div>
            </div>
        </Dialog>
    );
}

export default HLServerSearchListDialog;